import {Core} from '@strapi/strapi';
import {StringSession} from "telegram/sessions";
import {Api, TelegramClient} from "telegram";
import ChannelParticipant = Api.ChannelParticipant;
import type {User} from "@telegraf/types/manage";
import {SynchronizationStatus} from "../content-type/telegram-group";
import {KoniBot} from "../../bot/koni-bot";
import {Context} from "telegraf";
import {Update} from "@telegraf/types";

const APP_ID = Number(process.env.TELEGRAM_APP_CRAWLER_ID);
const APP_HASH = process.env.TELEGRAM_APP_CRAWLER_HASH;
const USER_SESSION = process.env.TELEGRAM_APP_CRAWLER_USER_SESSION;
const BOT_SESSION = process.env.TELEGRAM_APP_CRAWLER_BOT_SESSION;
const APP_BOT_TOKEN = process.env.TELEGRAM_APP_CRAWLER_BOT_TOKEN;
const LIMIT = 200;

const GROUP_UID = 'plugin::telegram-bot.telegram-group';

type UserGroupInfo = User & {
  msgCount: number;
  joinDate: number;
}

const SynchronizeActionService = ({strapi}: { strapi: Core.Strapi }) => ({
  telegramUser: strapi.plugin('telegram-bot').service('telegram-user'),
  telegramGroup: strapi.plugin('telegram-bot').service('telegram-group'),
  telegramUserActivity: strapi.plugin('telegram-bot').service('telegram-user-activity'),
  koniBot: null as KoniBot | null,

  async registerCrawlerBotActions() {
    strapi.log.info('[Telegram Crawler Bot] Registering crawler bot actions');
    const koniBot = new KoniBot(APP_BOT_TOKEN);
    const bot = koniBot.bot;
    bot.on('message', this.handleMessage.bind(this));
    this.koniBot = koniBot;
  },

  async launchCrawlerBot() {
    strapi.log.info('[Telegram Crawler Bot] Launch bot actions');
    if (this.koniBot) {
      await this.koniBot.launch();
    }
  },

  // Stop crawler bot
  async stopCrawlerBot() {
    strapi.log.info('[Telegram Crawler Bot] Stop bot actions');
    if (this.koniBot) {
      await this.koniBot.stop();
    }
  },

  // Handle user send message
  async handleUserSendMessage(ctx: Context<Update.MessageUpdate>) {
    const {chat, from} = ctx.update.message;
    const groupId = chat.id;
    const userId = from.id;

    const groupInfo = await this.telegramGroup.getGroupByTelegramId(groupId);
    if (!groupInfo) {
      return;
    }

    let userInfo = await this.telegramUser.getUserByTelegramId(userId);
    if (!userInfo) {
      userInfo = await this.telegramUser.upsertUser(from);
    }

    await this.telegramUserActivity.updateMessageCount(groupInfo.id, userInfo.id);
  },

  async handleMessage(ctx: Context<Update.MessageUpdate>) {
    // @ts-ignore
    const {new_chat_members, left_chat_member} = ctx.update.message;
    if (!new_chat_members && !left_chat_member) {
      await this.handleUserSendMessage(ctx as unknown as Context<Update.MessageUpdate>);
    }

  },

  async getTelegramClientUser() {
    if (!APP_ID || !APP_HASH || !USER_SESSION) {
      strapi.log.error("SynchronizeActionService: Please provide APP_ID, APP_HASH, USER_SESSION in .env file");
      return null;
    }

    const stringSession = new StringSession(USER_SESSION); // Session string
    const client = new TelegramClient(stringSession, APP_ID, APP_HASH, {
      connectionRetries: 1,
    });

    try {
      await client.start({
        botAuthToken: undefined,
        phoneNumber: '',
      });

      strapi.log.info("You should now be connected.");
      return client;
    } catch (e) {
      strapi.log.error(e)
      return null;
    }
  },

  async getTelegramClientBot() {
    if (!APP_ID || !APP_HASH || !BOT_SESSION) {
      strapi.log.error("SynchronizeActionService: Please provide APP_ID, APP_HASH, BOT_SESSION in .env file");
      return null;
    }

    const stringSession = new StringSession(BOT_SESSION); // Session string
    const client = new TelegramClient(stringSession, APP_ID, APP_HASH, {
      connectionRetries: 1,
    });

    try {
      await client.start({
        botAuthToken: undefined,
      });

      strapi.log.info("You should bot now be connected.");
      return client;
    } catch (e) {
      strapi.log.error(e)
      return null;
    }
  },

  // Get user info with group url
  async getUserInfoInGroup(clientBot: TelegramClient, groupUrl: string) {
    const group = await clientBot.getEntity(groupUrl);
    if (!group) {
      return null;
    }

    let offset = 0;

    const userMap: Record<string, UserGroupInfo> = {};

    while (true) {
      const result = await clientBot.invoke(
        new Api.channels.GetParticipants({
          channel: group,
          filter: new Api.ChannelParticipantsSearch({q: ''}), // Lấy tất cả thành viên gần đây
          offset: offset,
          limit: LIMIT,
        })
      ) as Api.channels.ChannelParticipants;
      const participantList = result.participants as ChannelParticipant[];

      const userList = result.users as unknown as User[];
      for (const user of userList) {
        const {username, firstName, lastName, langCode, id, premium, bot} = user as unknown as Api.User;
        let userId = Number(id);
        // Todo: Get user photo and save to database
        if (userId && !userMap[userId]) {
          userMap[userId] = {
            id: userId,
            username,
            first_name: firstName,
            last_name: lastName,
            language_code: langCode,
            is_premium: premium ?? false,
            is_bot: bot,
            msgCount: 0,
            joinDate: 0
          } as UserGroupInfo;
        }
      }

      // Update join date for each user
      participantList.forEach((participant) => {
        // @ts-ignore
        const {date, peer} = participant;
        let userId = Number(participant.userId);
        if (!userId && peer && peer.userId) {
          userId = Number(peer.userId);
        }
        if (userMap[userId]) {
          userMap[userId].joinDate = date;
        }
      });

      if (!result.participants || result.participants.length === 0) {
        break; // Break the loop if participants is empty
      }
      offset += result.participants.length; // Update offset get next participants
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    return userMap;
  },

  // Get user info with group url
  // Return list of user info and message count
  async getMessageWithGroup(clientUser: TelegramClient, clientBot: TelegramClient, groupUrl: string) {
    const group = await clientBot.getEntity(groupUrl);
    if (!group) {
      return null;
    }

    // Get user info
    const userMap: Record<string, UserGroupInfo> = await this.getUserInfoInGroup(clientBot, groupUrl);
    let offsetMessageId = 0;
    const groupUser = await clientUser.getEntity(groupUrl);
    // get message count of each user
    while (true) {
      const result = await clientUser.invoke(
        new Api.messages.GetHistory({
          peer: groupUser,
          offsetId: offsetMessageId,
          offsetDate: 0,
          addOffset: 0,
          limit: LIMIT,
          maxId: 0,
          minId: 0
        })
      ) as Api.messages.Messages;

      // if message is empty, break the loop
      if (!result.messages || result.messages.length === 0) {
        break;
      }

      const messageList = result.messages as Api.Message[];
      for (let message of messageList) {
        const user = message.fromId as Api.PeerUser;
        if (user && user.userId) {
          const userId = Number(user.userId);
          if (userId && userMap[userId]) {
            userMap[userId].msgCount += 1;
          }
        }
      }
      // Update offsetId to get next messages
      offsetMessageId = result.messages[result.messages.length - 1].id;

      // Delay 500ms to avoid rate limit
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    return Object.values(userMap) as UserGroupInfo[];
  },

  async synchronizeUserAction() {
    strapi.log.info("Start synchronize user action");
    // if group is running, return
    const groupRunningList = await strapi.documents(GROUP_UID).findMany({
      filters: {
        synchronization_status: {
          $in: [SynchronizationStatus.PENDING, SynchronizationStatus.SYNCING]
        }
      }
    });
    if (groupRunningList && groupRunningList.length > 0) {
      strapi.log.info("Group is running, please wait for it to finish");
      return [];
    }

    const groups = await strapi.documents(GROUP_UID).findMany({
      filters: {
        synchronization_status: SynchronizationStatus.SYNCED
      }
    });

    if (!groups || groups.length === 0) {
      return [];
    }

    const clientBot = await this.getTelegramClientBot();
    if (!clientBot) {
      return [];
    }

    for (const group of groups) {
      const groupUrl = group.group_url;
      if (!groupUrl) continue;
      const userList = Object.values(await this.getUserInfoInGroup(clientBot, groupUrl));
      const existedActivities = await this.telegramUserActivity.getActivitiesByGroup(group.id);
      const existedMap: Record<string, {documentId: string, isJoined: boolean, inList: boolean}> = existedActivities.reduce((acc, item) => {
        if (item.user_id){
          acc[item.user_id.telegram_id] = {
            documentId: item.documentId,
            isJoined: item.isJoined,
            inList: false
          };
        }
        return acc;
      }, {});
      if (userList && userList.length > 0) {
        for (const user of userList) {
          const existed = existedMap[user.id];
          if (!existed) {
            // Save user info if not existed
            const existed = await this.telegramUser.upsertUser(user);
            // Save user activity
            await this.telegramUserActivity.upsertActivity({
              groupId: group.id,
              userId: existed.id,
              isJoined: true,
              timeSinceJoin: user.joinDate * 1000,
            });
          } else {
            existed.inList = true;
            // Update user join group
            if (!existed.isJoined) {
              await this.telegramUserActivity.updateActivity(existed.documentId, {is_joined: true});
            }
          }
        }

        // Update user left group
        for (const e of Object.values(existedMap)) {
          if (!e.inList) {
            await this.telegramUserActivity.updateActivity(e.documentId, {is_joined: false});
          }
        }
      }
    }

    try {
      // Disconnect
      await clientBot.destroy();
      strapi.log.info("Disconnect from telegram client get user info");
    } catch (e) {
      strapi.log.error(e);
    }
  },
  async synchronizeMessageAction() {
    strapi.log.info("Start synchronize message action");

    // Get all groups that have pending synchronization status pending
    const groups = await strapi.documents(GROUP_UID).findMany({
      filters: {
        synchronization_status: SynchronizationStatus.PENDING
      }
    });

    if (!groups || groups.length === 0) {
      return [];
    }

    const clientUser = await this.getTelegramClientUser();
    if (!clientUser) {
      return [];
    }

    const clientBot = await this.getTelegramClientBot();
    if (!clientBot) {
      return [];
    }

    for (const group of groups) {
      const groupUrl = group.group_url;
      if (!groupUrl) continue;

      try {
        await this.telegramGroup.updateSynchronizationStatus(group.documentId, SynchronizationStatus.SYNCING);
        const userList = await this.getMessageWithGroup(clientUser, clientBot, groupUrl);
        // Get user info and save to database
        if (userList && userList.length > 0) {
          for (const user of userList) {
            // Save user info if not existed
            const existed = await this.telegramUser.upsertUser(user);

            // Save user activity
            await this.telegramUserActivity.upsertActivity({
              groupId: group.id,
              userId: existed.id,
              messageCount: user.msgCount,
              timeSinceJoin: user.joinDate * 1000,
              isJoined: true
            });
          }
        }

        await this.telegramGroup.updateSynchronizationStatus(group.documentId, SynchronizationStatus.SYNCED);
      } catch (e) {
        strapi.log.error(e);
        await this.telegramGroup.updateSynchronizationStatus(group.documentId, SynchronizationStatus.SYNCED);
      }
    }

    try {
      // Disconnect
      await Promise.all([
        clientUser.destroy(),
        clientBot.destroy()
      ]);
      strapi.log.info("Disconnect from telegram client");
    } catch (e) {
      strapi.log.error(e);
    }
    return {};
  }

});

export type SynchronizeActionServiceType = ReturnType<typeof SynchronizeActionService>;

export default SynchronizeActionService;
